// Signing the week (round 44). MANDATORY: the worker's own submit carries
// their signature every time, and the server refuses one without it.
//
// The totals are said above the pad, in words, so what is being signed is
// on the screen while it is signed.
import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { SignaturePad } from '../forms/SignaturePad';
import { minutesToHm, type WeekTotals } from '../../lib/timesheetHours';

export const DECLARATION = 'I declare these hours are a true record of the time I worked this week.';

export function SignAndSubmitModal({
  weekLabel,
  totals,
  busy,
  error,
  onSubmit,
  onClose,
}: {
  weekLabel: string;
  totals: WeekTotals;
  busy: boolean;
  error: string | null;
  onSubmit: (signature: Blob) => void;
  onClose: () => void;
}) {
  const [signature, setSignature] = useState<Blob | null>(null);

  const parts = [`${minutesToHm(totals.workedMinutes)} worked`];
  if (totals.breakMinutes > 0) parts.push(`${minutesToHm(totals.breakMinutes)} unpaid break`);
  if (totals.leaveMinutes > 0) parts.push(`${minutesToHm(totals.leaveMinutes)} leave`);
  if (totals.fullLeaveDays > 0) parts.push(`${totals.fullLeaveDays} full day${totals.fullLeaveDays === 1 ? '' : 's'} of leave`);

  return (
    <Modal
      title="Sign and submit"
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-ghost" onClick={onClose} disabled={busy}>
            Not yet
          </button>
          <button
            type="button"
            className="btn-primary"
            disabled={!signature || busy}
            onClick={() => signature && onSubmit(signature)}
          >
            {busy ? 'Submitting…' : 'Submit timesheet'}
          </button>
        </>
      }
    >
      <p>
        Week of {weekLabel}: <strong>{parts.join(', ')}</strong>.
      </p>
      <p className="text-sm text-gray-700">{DECLARATION}</p>
      {error && (
        <p className="banner-error" role="alert">
          {error}
        </p>
      )}
      <p className="form-label">Sign with your finger</p>
      {/* Live: every stroke hands the pad's PNG over, so Submit lights up as
          soon as there is a signature, with no confirm step between. */}
      <SignaturePad live disabled={busy} onSigned={setSignature} onCleared={() => setSignature(null)} />
      {!signature && <p className="hint">Submit unlocks once you have signed.</p>}
    </Modal>
  );
}
